/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ClusterService } from '../../services/utilities';
import { PulseHAStatus } from '../../enums';
import { Router } from '@angular/router';
import { NavigationService, RbacService, UtilsService } from '../../../../../core/services';
import { WidgetBaseComponent } from '../widgetBase.component';
import { WidgetInterface } from '../../interfaces/widget.interface';
import { WidgetStateEnum } from '../../../../../core/enums';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-widget-pulse-ha',
    templateUrl: './pulse-ha.widget.html',
    styleUrls: ['../../../../../core/components/widget/widget.component.scss']
})

export class PulsehaWidgetComponent extends WidgetBaseComponent implements AfterViewInit, OnDestroy, WidgetInterface {
    /**
     * Get widget table container.
     */
    @ViewChild('list', { static: true }) public list: ElementRef;

    /**
     * Store how many members are online.
     */
    public online: number = 0;

    /**
     * Store how many members are offline.
     */
    public offline: number = 0;

    /**
     * Loading state of the app.
     */
    public loading: boolean;

    /**
     * Sore all of our members to be listed in the widget.
     */
    public members: any[];

    /**
     * Set our default state.
     */
    public state: WidgetStateEnum;

    /**
     * Determine whether to show a larger list or not.
     */
    public scrolled: boolean;

    /**
     * Store the nodes in our cluster.
     */
    public nodes: any[];

    /**
     * Store permissions for buttons.
     */
    public permissionsLookup: any = {};

    /**
     * Store interval for clean up.
     */
    private readonly clusterStatusInterval: any;

    public constructor(
        private clusterService: ClusterService,
        private navigationService: NavigationService,
        private rbacService: RbacService,
        private router: Router,
        utils: UtilsService
    ) {
        super(utils);

        this.loading = true;
        this.resetLocalVariables();
        this.registerButtons();
        this.nodes = this.clusterService.nodes;
        if (this.nodes) {
            this.updateNodesStatus(this.nodes);
        }
    }

    public ngOnDestroy() {
        clearInterval(this.clusterStatusInterval);
    }

    public ngAfterViewInit(): void {
        const element = this.list.nativeElement;
        if (element) {
            element.addEventListener('scroll', (event: any) => {
                if (event.target.scrollTop >= 11) {
                    this.scrolled = true;
                }
            });
        }
    }

    /**
     * Sets the status bullet next to the nodes.
     * @param member
     */
    public pulseHaHtml(member: { status: string; }) {
        return `<div class="status-bullet ${this.utils.getStatusDisplayName(member.status)}"></div>`;
    }

    /**
     * Get our widget details via the api.
     */
    public getWidgetDetails(contentEnabled: boolean): void {
        if (contentEnabled) {
            // Subscribe to HA status changes
            this.clusterService.nodesEmitter
                .pipe(takeUntil(this.destroyed$))
                .subscribe((nodes) => {
                    this.nodes = nodes;
                    this.updateNodesStatus(nodes);
                });
        }
    }

    /**
     * Reset our local variables.
     */
    public resetLocalVariables(): void {
        this.online = 0;
        this.offline = 0;
        this.members = [];
        this.state = WidgetStateEnum.OK;
    }

    /**
     * Set up socket listeners.
     */
    public socketListeners(): void {
    }

    /**
     * Get the online node and redirect to HAConfig page.
     * @param status
     */
    public getNodeAndRedirect(status: any) {
        if (this.nodes && this.permissionsLookup['pulse-widget-node-redirect']) {
            this.redirectToHAConfig(status);
        }
    }

    /**
     * Redirect to HAConfig page with filter applied.
     * @param filter
     */
    public redirectToHAConfig(filter: any) {
        if (this.permissionsLookup['pulse-widget-node-redirect']) {
            this.router.navigate(
                [this.navigationService.getEnduranceUrl('/cluster-configuration/ha-config')],
                { queryParams: { sFilter: filter } }
            ).then();
        }
    }

    /**
     * Set the number of node for online and offline status.
     * @param nodes
     * @private
     */
    private updateNodesStatus(nodes: any[]): void {
        const active = PulseHAStatus.ACTIVE.toLowerCase();
        const passive = PulseHAStatus.PASSIVE.toLowerCase();
        this.online = nodes.filter(node => node.status === active || node.status === passive).length;

        // Nodes length minus the online count give you the offline count.
        if (this.online <= nodes.length) {
            this.offline = nodes.length - this.online;
        }

        this.members = nodes;
        this.loading = false;
    }

    /**
     * Register buttons.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'pulse-widget-node-redirect': [{ path: '/status', methods: ['GET'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
